import { format } from 'date-fns';
import { toJS } from 'mobx';
import { types as t, flow, getParent, getParentOfType } from 'mobx-state-tree';

import { Integration } from 'models/integrations';
import { withEnv } from 'models/withEnv';

function valueDict(data) {
  return data.reduce((agg, entry) => {
    if (entry.type === 'multiple') {
      if (entry.value) {
        agg[entry.name] = toJS(entry.value);
      } else {
        agg[entry.name] = [];
      }
    } else if (entry.type === 'repeating') {
      let groups = [];
      entry.fields.forEach((group) => {
        let fields = {};
        group.forEach((field) => {
          fields[field.name] = field.value;
        });
        groups.push(fields);
      });
      agg[entry.name] = groups;
    } else {
      agg[entry.name] = entry.value;
    }
    return agg;
  }, {});
}

const IntegrationDataSource = t.model('IntegrationDataSource', {
  integration: t.string,
  field: t.string,
});

export const IntegrationData = t
  .model('IntegrationData', {
    name: '',
    type: '',
    label: '',
    checkboxLabel: '',
    value: t.maybeNull(
      t.optional(
        t.union(t.string, t.number, t.boolean, t.Date, t.array(t.string), t.array(t.frozen())),
        ''
      )
    ),
    fields: t.maybe(t.array(t.array(t.late(() => IntegrationData)))),
    previewFileName: t.maybeNull(t.string),
    required: t.maybe(t.boolean),
    choices: t.frozen(),
    dynamic: t.maybe(t.boolean),
    blob: t.optional(t.frozen(), null),
    loadedBlob: false,
    fileUrl: t.maybeNull(t.string),
    error: '',
    validators: t.array(t.string),
    alertType: '',
    source: t.maybeNull(IntegrationDataSource),
    status: '',
  })
  .views(withEnv)
  .views((self) => ({
    get parent() {
      return getParentOfType(self, Integration);
    },
    get inputValue() {
      if (self.value && (self.type === 'date' || self.type === 'dateField')) {
        return format(new Date(self.value), 'EEEE, MMMM do yyyy');
      } else if (self.choices) {
        return self.getText(self.value);
      }
      return self.value;
    },
    get defaultValue() {
      const choicesObj = self.choices.reduce((o, [key, value]) => {
        o[key] = value;
        return o;
      }, {});
      if (Array.isArray(self.value)) {
        return self.value.map((v) => {
          if (typeof v === 'object') {
            return { value: v.value, label: choicesObj[v.value] };
          }
          return { value: v, label: choicesObj[v] };
        });
      }
      if (typeof self.value === 'object') {
        return self.value;
      } else if (self.value === '') {
        return null;
      }
      return { value: self.value, label: choicesObj[self.value] };
    },
    get fileName() {
      if (self.previewFileName) {
        return self.previewFileName;
      } else if (self.type === 'file' || self.type === 'fileurl') {
        return self.value.substring(self.value.lastIndexOf('/') + 1, self.value.indexOf('?'));
      }
      return self.value;
    },
    get choicesToObj() {
      return self.choices.map((choice) => {
        return { value: String(choice[0]), label: choice[1] };
      });
    },
  }))
  .actions((self) => ({
    getText(value) {
      for (let i = 0; i < self.choices.length; i++) {
        if (Number(self.choices[i][0]) === Number(value)) {
          return self.choices[i][1];
        }
      }
      return value;
    },
    valid() {
      const hasValue =
        self.value !== '' && self.value !== undefined && self.value !== null && self.value;
      if (self.type !== 'hidden' && self.required && !hasValue) {
        self.setError(self.env.t('errors.required'));
        return false;
      }
      return true;
    },
    setError(error) {
      self.error = error;
    },
    setValue(value) {
      self.error = '';
      if (self.type === 'multiple') {
        self.value = value.map((v) => v.value);
      } else {
        self.value = value;
      }
    },
    refetch(field) {
      getParentOfType(self, Integration).refetchForApplication(
        valueDict(getParent(self, 2).items),
        getParent(self, 2).id,
        field
      );
    },
    loadBlob: flow(function* () {
      if (self.loadedBlob) return;
      if (!self.value) {
        self.loadedBlob = true;
        return;
      }

      const res = yield self.env.api.applications.loadBlob(self.value);
      if (res.status === 200) {
        self.blob = res.data;
      }
      self.loadedBlob = true;
    }),
  }));
